import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Section } from '../PageShell.tsx';
import { Modal } from '../Modal.tsx';
import { EmptyState, Spinner } from '../ui.tsx';
import { PlusIcon, TrashIcon } from '../icons.tsx';
import { api } from '../../lib/api.ts';
import { saveError } from './shared.tsx';

interface AutoResponseRow {
  id: number;
  enabled: boolean;
  source: string;
  condition: string;
  equation: string;
  phrase: string;
  response: string;
}

interface PresetRow {
  id: number;
  title: string;
  content: string;
  shortcut: string | null;
}

export function AutomationTab({ isOwner }: { isOwner: boolean }): React.JSX.Element {
  const queryClient = useQueryClient();
  const [preset, setPreset] = useState({ title: '', content: '' });
  const [rule, setRule] = useState({ phrase: '', response: '' });
  const [ruleOpen, setRuleOpen] = useState(false);
  const [presetOpen, setPresetOpen] = useState(false);

  const autoResponses = useQuery({
    queryKey: ['settings', 'auto-responses'],
    queryFn: () => api.get<{ items: AutoResponseRow[] }>('/settings/auto-responses'),
  });

  const presets = useQuery({
    queryKey: ['preset-responses'],
    queryFn: () => api.get<{ items: PresetRow[] }>('/settings/preset-responses'),
  });

  const addRule = useMutation({
    mutationFn: () =>
      api.post('/settings/auto-responses', {
        enabled: true,
        source: 'any',
        condition: 'message',
        equation: 'contains',
        ...rule,
      }),
    onSuccess: () => {
      setRule({ phrase: '', response: '' });
      setRuleOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['settings', 'auto-responses'] });
    },
  });

  const removeRule = useMutation({
    mutationFn: (id: number) => api.delete(`/settings/auto-responses/${String(id)}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['settings', 'auto-responses'] });
    },
  });

  const addPreset = useMutation({
    mutationFn: () => api.post('/settings/preset-responses', { ...preset, shortcut: null, position: 0 }),
    onSuccess: () => {
      setPreset({ title: '', content: '' });
      setPresetOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['preset-responses'] });
    },
  });

  const removePreset = useMutation({
    mutationFn: (id: number) => api.delete(`/settings/preset-responses/${String(id)}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['preset-responses'] });
    },
  });

  return (
    <>
      <Section
        title="Auto responses"
        description="When a customer's message contains a phrase, reply automatically."
        actions={
          isOwner ? (
            <button
              type="button"
              className="lg-btn-secondary"
              onClick={() => {
                setRuleOpen(true);
              }}
            >
              <PlusIcon className="h-4 w-4" />
              Add auto response
            </button>
          ) : null
        }
      >
        {autoResponses.isLoading ? (
          <Spinner className="h-5 w-5 text-brand-600" />
        ) : (autoResponses.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No auto responses yet" />
        ) : (
          <ul className="divide-y divide-slate-200 dark:divide-slate-800">
            {(autoResponses.data?.items ?? []).map((item) => (
              <li key={item.id} className="flex items-start gap-3 py-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-slate-800 dark:text-slate-100">
                    <span className="text-slate-500 dark:text-slate-400">contains</span> “{item.phrase}”
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{item.response}</p>
                </div>
                {isOwner ? (
                  <button
                    type="button"
                    className="lg-btn-ghost px-2 hover:text-rose-600"
                    onClick={() => {
                      removeRule.mutate(item.id);
                    }}
                  >
                    <TrashIcon className="h-4 w-4" />
                    <span className="sr-only">Delete</span>
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section
        title="Preset replies"
        description="Snippets agents can drop into a conversation from the composer."
        actions={
          isOwner ? (
            <button
              type="button"
              className="lg-btn-secondary"
              onClick={() => {
                setPresetOpen(true);
              }}
            >
              <PlusIcon className="h-4 w-4" />
              Add preset reply
            </button>
          ) : null
        }
      >
        {presets.isLoading ? (
          <Spinner className="h-5 w-5 text-brand-600" />
        ) : (presets.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No preset replies yet" />
        ) : (
          <ul className="divide-y divide-slate-200 dark:divide-slate-800">
            {(presets.data?.items ?? []).map((item) => (
              <li key={item.id} className="flex items-start gap-3 py-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{item.title}</p>
                  <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{item.content}</p>
                </div>
                {isOwner ? (
                  <button
                    type="button"
                    className="lg-btn-ghost px-2 hover:text-rose-600"
                    onClick={() => {
                      removePreset.mutate(item.id);
                    }}
                  >
                    <TrashIcon className="h-4 w-4" />
                    <span className="sr-only">Delete</span>
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Modal
        open={ruleOpen}
        title="Add an auto response"
        description="Sent the moment a customer's message contains the phrase, whichever channel it came in on."
        onClose={() => {
          setRuleOpen(false);
          addRule.reset();
        }}
        footer={
          <>
            <button
              type="button"
              className="lg-btn-secondary"
              onClick={() => {
                setRuleOpen(false);
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="rule-form"
              className="lg-btn-primary"
              disabled={addRule.isPending || !rule.phrase.trim() || !rule.response.trim()}
            >
              {addRule.isPending ? <Spinner /> : null}
              Add auto response
            </button>
          </>
        }
      >
        <form
          id="rule-form"
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            addRule.mutate();
          }}
        >
          {saveError(addRule.error) ? (
            <p
              className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:bg-rose-950/50 dark:text-rose-200"
              role="alert"
            >
              {saveError(addRule.error)}
            </p>
          ) : null}

          <div>
            <label className="lg-label" htmlFor="rule-phrase">
              When the message contains
            </label>
            <input
              id="rule-phrase"
              className="lg-input"
              placeholder="hours"
              value={rule.phrase}
              onChange={(event) => {
                setRule((previous) => ({ ...previous, phrase: event.target.value }));
              }}
            />
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Matched anywhere in the message, ignoring case.
            </p>
          </div>

          <div>
            <label className="lg-label" htmlFor="rule-response">
              Reply with
            </label>
            <textarea
              id="rule-response"
              className="lg-input min-h-24 resize-y"
              placeholder="We're open 8am–6pm on weekdays."
              value={rule.response}
              onChange={(event) => {
                setRule((previous) => ({ ...previous, response: event.target.value }));
              }}
            />
          </div>
        </form>
      </Modal>

      <Modal
        open={presetOpen}
        title="Add a preset reply"
        description="Agents pick these from the composer instead of retyping the same answer."
        onClose={() => {
          setPresetOpen(false);
          addPreset.reset();
        }}
        footer={
          <>
            <button
              type="button"
              className="lg-btn-secondary"
              onClick={() => {
                setPresetOpen(false);
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="preset-form"
              className="lg-btn-primary"
              disabled={addPreset.isPending || !preset.title.trim() || !preset.content.trim()}
            >
              {addPreset.isPending ? <Spinner /> : null}
              Add preset reply
            </button>
          </>
        }
      >
        <form
          id="preset-form"
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            addPreset.mutate();
          }}
        >
          {saveError(addPreset.error) ? (
            <p
              className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:bg-rose-950/50 dark:text-rose-200"
              role="alert"
            >
              {saveError(addPreset.error)}
            </p>
          ) : null}

          <div>
            <label className="lg-label" htmlFor="preset-title">
              Title
            </label>
            <input
              id="preset-title"
              className="lg-input"
              placeholder="Opening hours"
              value={preset.title}
              onChange={(event) => {
                setPreset((previous) => ({ ...previous, title: event.target.value }));
              }}
            />
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              What the agent sees in the list — the message itself stays hidden until they pick it.
            </p>
          </div>

          <div>
            <label className="lg-label" htmlFor="preset-content">
              Message
            </label>
            <textarea
              id="preset-content"
              className="lg-input min-h-32 resize-y"
              value={preset.content}
              onChange={(event) => {
                setPreset((previous) => ({ ...previous, content: event.target.value }));
              }}
            />
          </div>
        </form>
      </Modal>
    </>
  );
}



/**
 * The readable half of a rejected save.
 *
 * A validation failure carries "Bad Request" as its message and the useful part
 * in `fieldErrors` — so a Twilio form submitted with the auth token left blank,
 * which is how it always comes back from the server, reported "Bad Request" and
 * left the owner to guess which of three fields it meant.
 */
